import React, { useState, useCallback, createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { parseFiltersFromQuery } from '../components/ideas/ideasHelpers';
import { useIdeaFilters } from './useIdeaFilters';

// Define specific types for filter objects
export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

export interface FilterConfig {
  name: string;
  label: string;
  type: 'checkbox' | 'date' | 'select' | 'fetchableCheckbox' | 'boolean' | 'number' | 'parent' | '_id';
  filterObject: 'evidence' | 'idea';
  dbFieldPath?: string;
  showIn360?: boolean;
  showInDiscovery?: boolean;
  enabled?: boolean;
  options?: FilterOption[];
  parent?: string;
  operations?: Array<{
    value: string;
    label: string;
    key?: string;
    name?: string;
  }>;
}

export interface ActiveFilter extends FilterConfig {
  selected: string[] | string | number | boolean | null | undefined | any;
  operator?: string;
  query?: any;
}

export interface FilterState {
  activeFilters: ActiveFilter[];
  selectedCategories: string[];
  selectedChildFilters: string[];
  selectedOptions: any[];
  selectedOperator: string | null;
  rangeDates: [Date | null, Date | null];
}

export interface UseFiltersReturn {
  // State
  activeFilters: ActiveFilter[];
  selectedCategories: string[];
  selectedChildFilters: string[];
  selectedOptions: any[];
  selectedOperator: string | null;
  rangeDates: [Date | null, Date | null];
  
  // Setters
  setActiveFilters: (filters: ActiveFilter[] | ((prev: ActiveFilter[]) => ActiveFilter[])) => void;
  setSelectedCategories: (categories: string[]) => void;
  setSelectedChildFilters: (childFilters: string[]) => void;
  setSelectedOptions: (options: any[]) => void;
  setSelectedOperator: (operator: string | null) => void;
  setRangeDates: (dates: [Date | null, Date | null]) => void;
  
  // Actions
  saveAndClose: () => FilterState;
  resetFilters: () => void;
  removeFilter: (filterName: string) => void;
  getCurrentFilterState: () => FilterState;
}

// Create a context for shared filter state
const FiltersContext = createContext<UseFiltersReturn | undefined>(undefined);

// Provider component
export const FiltersProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { ideasFiltersConfig } = useIdeaFilters();

  // Parse URL parameters to get initial filters (for change log filters)
  const initialFilters = useMemo(() => {
    try {
      const parsedFilters = parseFiltersFromQuery(location.search, ideasFiltersConfig);
      // Filter to only include evidence filters
      const evidenceFilters = parsedFilters?.filter((filter: any) => filter.filterObject === 'evidence') || [];
      console.log('🔍 [FiltersProvider] Parsed URL filters:', evidenceFilters?.length || 0);
      return evidenceFilters;
    } catch (error) {
      console.warn('Error parsing URL filters in FiltersProvider:', error);
      return [];
    }
  }, [location.search, ideasFiltersConfig]);

  // Filter state - initialize with URL filters
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>(initialFilters);

  // Update activeFilters when initialFilters changes (e.g., when ideasFiltersConfig loads)
  useEffect(() => {
    if (initialFilters.length > 0 && activeFilters.length === 0) {
      setActiveFilters(initialFilters);
    }
  }, [initialFilters]);


  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedChildFilters, setSelectedChildFilters] = useState<string[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<any[]>([]);
  const [selectedOperator, setSelectedOperator] = useState<string | null>(null);
  const [rangeDates, setRangeDates] = useState<[Date | null, Date | null]>([null, null]);

  // Enhanced setActiveFilters that supports both direct assignment and callback
  const setActiveFiltersEnhanced = useCallback((
    filters: ActiveFilter[] | ((prev: ActiveFilter[]) => ActiveFilter[])
  ) => {
    if (typeof filters === 'function') {
      setActiveFilters(filters);
    } else {
      setActiveFilters(filters);
    }
  }, []);

  // Get current filter state
  const getCurrentFilterState = useCallback((): FilterState => {
    return {
      activeFilters,
      selectedCategories,
      selectedChildFilters,
      selectedOptions,
      selectedOperator,
      rangeDates
    };
  }, [activeFilters, selectedCategories, selectedChildFilters, selectedOptions, selectedOperator, rangeDates]);

  // Save and close function - returns current state
  const saveAndClose = useCallback((): FilterState => {
    const currentState = getCurrentFilterState();
    return currentState;
  }, [getCurrentFilterState]);

  // Reset all filters
  const resetFilters = useCallback(() => {
    setActiveFilters([]);
    setSelectedCategories([]);
    setSelectedChildFilters([]);
    setSelectedOptions([]);
    setSelectedOperator(null);
    setRangeDates([null, null]);
  }, []);

  // Remove a specific filter
  const removeFilter = useCallback((filterName: string) => {
    setActiveFilters(prev => prev.filter(filter => filter.name !== filterName));
  }, []);

  const value: UseFiltersReturn = {
    // State
    activeFilters,
    selectedCategories,
    selectedChildFilters,
    selectedOptions,
    selectedOperator,
    rangeDates,
    
    // Setters
    setActiveFilters: setActiveFiltersEnhanced,
    setSelectedCategories,
    setSelectedChildFilters,
    setSelectedOptions,
    setSelectedOperator,
    setRangeDates,
    
    // Actions
    saveAndClose,
    resetFilters,
    removeFilter,
    getCurrentFilterState,
  };

  return React.createElement(FiltersContext.Provider, { value }, children);
};

// Hook to use the shared filters context
export const useFilters = (): UseFiltersReturn => {
  const context = useContext(FiltersContext);
  if (!context) {
    throw new Error('useFilters must be used within a FiltersProvider');
  }
  return context;
}; 